import { create } from 'zustand';
import { agendaService, AgendaData, AgendaSummary, ConvertToPracticeData } from '@/services/agendaService';
import { Appointment } from '@/services/appointmentService';
import { appointmentService } from '@/services/appointmentService';

interface AgendaState {
  selectedDate: Date;
  appointments: Appointment[];
  summary: AgendaSummary;
  loading: boolean;
  error: string | null;

  // Actions
  setSelectedDate: (date: Date) => void;
  goToToday: () => void;
  goToPreviousDay: () => void;
  goToNextDay: () => void;
  fetchAgenda: (date?: Date) => Promise<void>;
  fetchAgendaByDate: (dateString: string) => Promise<void>;
  createAppointment: (data: Partial<Appointment>) => Promise<Appointment>;
  updateAppointment: (id: string, data: Partial<Appointment>) => Promise<void>;
  deleteAppointment: (id: string) => Promise<void>;
  updateStatus: (id: string, status: string) => Promise<void>;
  convertToPractice: (appointmentId: string, data: ConvertToPracticeData) => Promise<any>;
  reset: () => void;
}

const initialSummary: AgendaSummary = {
  total: 0,
  scheduled: 0,
  confirmed: 0,
  inProgress: 0,
  completed: 0,
  cancelled: 0,
  noShow: 0,
};

const initialState = {
  selectedDate: new Date(),
  appointments: [],
  summary: initialSummary,
  loading: false,
  error: null,
};

export const useAgendaStore = create<AgendaState>((set, get) => ({
  ...initialState,

  setSelectedDate: (date: Date) => {
    set({ selectedDate: date });
    get().fetchAgenda(date);
  },

  goToToday: () => {
    const today = new Date();
    set({ selectedDate: today });
    get().fetchAgenda(today);
  },

  goToPreviousDay: () => {
    const { selectedDate } = get();
    const prevDay = new Date(selectedDate);
    prevDay.setDate(prevDay.getDate() - 1);
    set({ selectedDate: prevDay });
    get().fetchAgenda(prevDay);
  },

  goToNextDay: () => {
    const { selectedDate } = get();
    const nextDay = new Date(selectedDate);
    nextDay.setDate(nextDay.getDate() + 1);
    set({ selectedDate: nextDay });
    get().fetchAgenda(nextDay);
  },

  fetchAgenda: async (date?: Date) => {
    set({ loading: true, error: null });
    try {
      const targetDate = date || get().selectedDate;
      const dateString = targetDate.toISOString().split('T')[0];
      const response = await agendaService.getAgendaByDate(dateString);
      set({
        appointments: response.data?.appointments || [],
        summary: response.data?.summary || initialSummary,
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nel caricamento dell\'agenda',
        loading: false,
      });
    }
  },

  fetchAgendaByDate: async (dateString: string) => {
    set({ loading: true, error: null });
    try {
      const response = await agendaService.getAgendaByDate(dateString);
      set({
        selectedDate: new Date(dateString),
        appointments: response.data?.appointments || [],
        summary: response.data?.summary || initialSummary,
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nel caricamento dell\'agenda',
        loading: false,
      });
    }
  },

  createAppointment: async (data: Partial<Appointment>) => {
    set({ loading: true, error: null });
    try {
      const response = await appointmentService.createAppointment(data);
      const newAppointment = response.data;
      // Ricarica l'agenda per avere i dati aggiornati
      await get().fetchAgenda();
      return newAppointment;
    } catch (error: any) {
      set({ loading: false });
      throw error;
    }
  },

  updateAppointment: async (id: string, data: Partial<Appointment>) => {
    set({ loading: true, error: null });
    try {
      await appointmentService.updateAppointment(id, data);
      // Ricarica l'agenda per avere i dati aggiornati
      await get().fetchAgenda();
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nell\'aggiornamento dell\'appuntamento',
        loading: false,
      });
      throw error;
    }
  },

  deleteAppointment: async (id: string) => {
    set({ loading: true, error: null });
    try {
      await appointmentService.deleteAppointment(id);
      set((state) => ({
        appointments: state.appointments.filter((a) => a.id !== id),
        summary: {
          ...state.summary,
          total: state.summary.total - 1,
        },
        loading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nell\'eliminazione dell\'appuntamento',
        loading: false,
      });
      throw error;
    }
  },

  updateStatus: async (id: string, status: string) => {
    set({ loading: true, error: null });
    try {
      await appointmentService.updateAppointmentStatus(id, status);
      // Ricarica l'agenda per avere i dati aggiornati
      await get().fetchAgenda();
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nell\'aggiornamento dello stato',
        loading: false,
      });
      throw error;
    }
  },

  convertToPractice: async (appointmentId: string, data: ConvertToPracticeData) => {
    set({ loading: true, error: null });
    try {
      const response = await agendaService.convertToPractice(appointmentId, data);
      // Ricarica l'agenda per aggiornare l'appuntamento con il link alla pratica
      await get().fetchAgenda();
      return response.data;
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nella conversione in pratica',
        loading: false,
      });
      throw error;
    }
  },

  reset: () => {
    set(initialState);
  },
}));
